import { AnyAction } from 'redux';

// Data layer
import { setRequestStatus } from 'data-layer-cr/dist/network';
import CoverageCatalog from 'data-layer-cr/dist/models/CoverageCatalog';
import { fixLimit } from 'data-layer-cr/dist/functions/coverages';
import { getLoggedUserId } from 'data-layer-cr/dist/authentication';

// Redux
import { AsyncAppThunk, AsyncDispatch } from '../../ThunkTypes';

// Network
import processRequest from '../../../services/processRequest';
import { GET_COVERAGES_FOR_PRODUCT } from '../requestIdentifiers';

// Local Models
import CoverageResponse from '../../../models/responseTypes/CoverageResponse';

import {
  updateRequestingCoverageState,
  setCoverages,
  setCoveragesForProduct,
} from '../slice/coverageSlice';

/**
 * Routes the limit fixing logic to apply it to the entire response.
 *
 * @param response - the response to fix
 */
function fixResponse(response: CoverageResponse): CoverageResponse {
  return {
    ...response,
    coverages: response.coverages.map(
      (coverage): CoverageCatalog => ({
        ...coverage,
        limits: coverage.limits.map(fixLimit),
      }),
    ),
  };
}

/**
 *
 * @param userId - checks if userId exists
 */

export function checkUserId(userId: string): string {
  if (userId === '') {
    return getLoggedUserId();
  }
  return userId;
}

/**
 * Updates the coverage list for vehicles
 */
export const requestCoverages =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(updateRequestingCoverageState('requesting'));

    try {
      const response = await processRequest<CoverageResponse>({
        method: 'GET',
        url: `Catalog/products/coverages?Code=vehicle&userId=${getLoggedUserId()}`,
      });
      const fixedResponse = fixResponse(response);
      dispatch(setCoverages(fixedResponse.coverages));
      return dispatch(updateRequestingCoverageState('success'));
    } catch (e) {
      return dispatch(updateRequestingCoverageState('failure'));
    }
  };

/**
 * Updates the current coverage data for the provided product.
 *
 * @param product - The product id to use in the search
 */
export const requestCoveragesForProduct =
  (product: string, vehicleuse = ''): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    dispatch(setRequestStatus(GET_COVERAGES_FOR_PRODUCT, 'requesting'));
    const { quotation } = getState();
    let profileId = 0;
    let userId = '';

    if (quotation) {
      profileId = quotation.currentQuotation.quotationProfile.id;
      userId = quotation.currentQuotation.user.id;
    }

    // skip request if given empty parameter
    if (product === '') {
      return dispatch(setRequestStatus(GET_COVERAGES_FOR_PRODUCT, 'success'));
    }

    try {
      if (profileId === 0) {
        throw new Error('profileId cannot be 0');
      }

      /**
       * We have to check for cloning scenario and for checking quotationDetail scenario
       * For cloning, we need current logged in user, to display the right prices
       * For detail, we need the currentQuotation to display the price used for quotation.
       */
      const vehicleUseParameter =
        vehicleuse !== '' ? `&vehicleuse=${vehicleuse}` : '';
      const response = await processRequest<CoverageResponse>({
        method: 'GET',
        url: `Catalog/products/coverages?code=${product}&userId=${checkUserId(
          userId,
        )}&profileId=${profileId}${vehicleUseParameter}`,
      });
      const fixedResponse = fixResponse(response);
      dispatch(
        setCoveragesForProduct({
          product,
          coverageList: fixedResponse.coverages,
          configuration: fixedResponse.config,
        }),
      );
      return dispatch(setRequestStatus(GET_COVERAGES_FOR_PRODUCT, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(GET_COVERAGES_FOR_PRODUCT, 'failure'));
    }
  };
